import { useI18n } from "vue-i18n";
import html_iway from "./template.iway.html?raw";
import ErrorMessage from "../ErrorMessage";
import { computed, defineComponent, inject, onMounted, ref, type SetupContext } from "vue";
import { useRouter } from "vue-router";
import { priceFormatterKey } from "../../../infrastructure/price_formatter";
import { currentOrderProviderKey } from "../../../infrastructure/current_order_provider";
import { orderServiceKey } from "../../../infrastructure/gfo_api/order_service";
import type { OrderItemDto } from "../../../infrastructure/gfo_api/order_api";
import { productApiKey, type ProductDto } from "../../../infrastructure/gfo_api/product_api";

const app_theme = import.meta.env.VITE_APP_THEME;
let html = "";
if ("iway" == app_theme) {
  html = html_iway;
} else if ("lic" == app_theme) {
  html = html_iway;
} else if ("sak" == app_theme) {
  html = html_iway;
}

interface Props {
  allowRowDeletion?: boolean;
  showNextStep?: boolean;
  showGotoConfigurator?: boolean;
}

export default defineComponent({
  template: html,
  components: { ErrorMessage },
  props: {
    /**
     * Erlaube Warenkorb-Zeilen zu löschen
     */
    allowRowDeletion: {
      type: Boolean,
      default: false,
    },
    /**
     * Zeige einen Link an der zum nächsten Bestellschritt führt
     */
    showNextStep: {
      type: Boolean,
      default: false,
    },
    /**
     * Zeige einen Link zur Konfigurator-Seite an.
     * Dort kann eine Bestellung geändert werden
     */
    showGotoConfigurator: {
      type: Boolean,
      default: false,
    },
  },
  emits: {
    next: () => true,
  },
  setup(_props: Props, { emit }: SetupContext) {
    const { t } = useI18n();
    const router = useRouter();
    const currentOrderProvider = inject(currentOrderProviderKey);
    const priceFormatter = inject(priceFormatterKey);
    const productApi = inject(productApiKey);
    const orderService = inject(orderServiceKey);

    // Local vars
    const products = ref<ProductDto[]>([]);
    const error = ref<string>("");

    // computed
    const order = computed(() => currentOrderProvider?.currentOrder?.value ?? null);
    const items = computed(() => order.value?.items ?? []);

    const productPos = function (productId: number): number {
      const product = products.value.find((p) => p.id == productId);
      return product ? product.pos : 2000;
    };
    const productTitle = function (productId: number): string {
      const product = products.value.find((p) => p.id == productId);
      return product ? product.title : "";
    };
    const productIsMain = function (productId: number): boolean {
      const product = products.value.find((p) => p.id == productId);
      return product ? product.is_main : false;
    };

    const sortedItems = computed(() => {
      return [...items.value].sort(
        (a, b) => productPos(a.product_id) - productPos(b.product_id),
      );
    });
    const showMinimumContractPeriod12M = computed(() => currentOrderProvider?.currentOrder.value?.tenant_context?.show_minimum_contract_period_12M ?? false);


    // methods

    const getRows = function (preSubtotal: boolean): OrderItemDto[] {
      return sortedItems.value.filter((item) => {
        if (0 == item.qty) {
          return false;
        }
        return preSubtotal
          ? productPos(item.product_id) < 800
          : productPos(item.product_id) > 800;
      });
    };

    const getCartRows = function (preSubtotal: boolean) {
      const rows = getRows(preSubtotal);
      if (!preSubtotal) {
        return rows;
      }
      let currentProductId = 0;
      const outRows: (OrderItemDto | { title: string; reference: string })[] = [];
      rows.forEach((item) => {
        if (item.product_id != currentProductId) {
          currentProductId = item.product_id;
          if (productIsMain(item.product_id)) {
            outRows.push({
              title: productTitle(item.product_id),
              reference: "PRODUKTTITEL",
            });
          }
        }
        outRows.push(item);
      });
      return outRows;
    };

    const sumRows = function (rows: OrderItemDto[]) {
      return rows.reduce(
        (sum, item) => ({
          fix: sum.fix + item.price_fix,
          rec: sum.rec + item.price_rec,
        }),
        { fix: 0, rec: 0 },
      );
    };

    const showSubtotal = computed(() => 0 < getRows(false).length);
    const subtotal = computed(() => sumRows(getRows(true)));
    const total = computed(() => {
      const sub = subtotal.value;
      const rest = sumRows(getRows(false));
      return { fix: sub.fix + rest.fix, rec: sub.rec + rest.rec };
    });

    // methods
    const removeRow = async function (row: OrderItemDto) {
      if (null != order.value?.ordered) {
        console.log("Bestellung abgeschlossen, änderung nicht möglich");
        return;
      }
      await orderService?.removeFromCart(
        row.product_variant_id,
        row.reference,
        row.qty,
      );
    };

    /**
     * Cart darf nicht direkt zum nächsten Schritt gehen.
     * Die Parent-Komponente muss validiert werden.
     * Darum sendet der Cart einen Event an die Parent-Komponente.
     */
    const nextStep = function () {
      if (null != order.value?.ordered) {
        console.log("Bestellung abgeschlossen, änderung nicht möglich");
        return;
      }
      emit("next");
    };

    const gotoConfigurator = function () {
      if (null != order.value?.ordered) {
        console.log("Bestellung abgeschlossen, änderung nicht möglich");
        return;
      }
      router.push("/");
    };

    onMounted(async () => {
      try {
        products.value = (await productApi?.getAllCached()) || [];
      } catch (err) {
        error.value = "General_Api_Failed";
      }
    });

    return {
      error,
      getCartRows,
      getRows,
      gotoConfigurator,
      order,
      priceFormatter,
      nextStep,
      removeRow,
      showMinimumContractPeriod12M,
      subtotal,
      showSubtotal,
      t,
      total,
    };
  },
});
